import React, { useEffect, useState } from "react";
import { FolderPlus, Loader2, Save, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * SaveAgreementsToProjectDialog — visually and behaviorally identical
 * to the Financial Tools "Save Result to Projects" modal.
 *
 *   • SAVE AS: display label the founder sees on the Projects card
 *              (pre-filled with the brand name from the intake).
 *   • SAVE INTO FOLDER: dropdown of existing folders + "Root of Projects"
 *              + inline "new folder name → Create" flow.
 *   • Save Result: attaches the intake to the chosen folder (or root)
 *              via `POST /agreements/plan/{intakeId}/attach-to/{folderId}`.
 *
 * "Root of Projects (no folder)" saves against the founder's dedicated
 * root container (auto-created on first save) so the intake still
 * lands somewhere queryable — folders and projects are the only
 * containers Mongo can attach `agreements_intakes` to.
 */
export default function SaveAgreementsToProjectDialog({
  open,
  onOpenChange,
  intakeProjectId,
  brandName,
  onSaved,
}) {
  const [displayName, setDisplayName] = useState("");
  const [folders, setFolders]         = useState([]);
  const [loadingFolders, setLoading]  = useState(true);
  const [selectedFolder, setSelected] = useState("");
  const [newFolder, setNewFolder]     = useState("");
  const [creating, setCreating]       = useState(false);
  const [saving, setSaving]           = useState(false);

  useEffect(() => {
    if (!open) return;
    setDisplayName(brandName && brandName !== "your brand" ? brandName : "Agreements Plan");
    setSelected("");
    setNewFolder("");
    setLoading(true);
    api.get("/projects")
      .then((r) => setFolders((r.data || []).filter((p) => p.is_folder)))
      .catch(() => setFolders([]))
      .finally(() => setLoading(false));
  }, [open, brandName]);

  const addFolder = async () => {
    const n = newFolder.trim();
    if (!n) return;
    setCreating(true);
    try {
      const r = await api.post("/projects", { name: n, is_folder: true });
      setFolders((prev) => [r.data, ...prev]);
      setSelected(r.data.id);
      setNewFolder("");
      toast.success(`Folder "${n}" created.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create folder.");
    } finally { setCreating(false); }
  };

  const confirm = async () => {
    const trimmed = (displayName || "").trim();
    if (!trimmed) { toast.error("Give this plan a name so you can find it later."); return; }
    setSaving(true);
    try {
      // Each Save creates its OWN wrapper project at the chosen
      // location (root or picked folder) so every submission has a
      // visible card in Projects rather than being hidden inside a
      // shared bucket. `type: "agreements_wrapper"` tells Projects.jsx
      // to render this card in the CPG Agreements style (brand name,
      // pink badge, Open→/agreements/plan) instead of the generic
      // project card.
      const wrapper = await api.post("/projects", {
        name: trimmed,
        parent_id: selectedFolder || null,
        type: "agreements_wrapper",
      });
      const targetId = wrapper.data?.id;
      if (!targetId) throw new Error("Could not create the project holder for this plan.");
      await api.post(
        `/agreements/plan/${encodeURIComponent(intakeProjectId)}/attach-to/${encodeURIComponent(targetId)}`,
        { display_name: trimmed },
      );
      toast.success(
        selectedFolder
          ? `Saved to Projects — “${trimmed}” inside the folder.`
          : `Saved to Projects — “${trimmed}” at the root.`,
      );
      onOpenChange?.(false);
      onSaved?.(targetId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Couldn't save.");
    } finally { setSaving(false); }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="save-agreements-modal"
    >
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
        <div className="p-6 border-b border-shell-border flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl grid place-items-center bg-primary/10 text-primary">
            <Save className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-2xl">Save Result to Projects</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Snapshot this plan into a Project. Saved results open in view-only mode so the details you built for <span className="font-semibold text-neutral-900">{brandName}</span> stay locked.
            </p>
          </div>
          <button onClick={() => onOpenChange?.(false)} className="p-1 rounded hover:bg-muted" aria-label="Close" data-testid="save-agreements-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">Save as</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              data-testid="save-agreements-name"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-shell-border outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">Save into folder</label>
            {loadingFolders ? (
              <div className="mt-2 text-xs text-muted-foreground inline-flex items-center gap-1.5">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading folders…
              </div>
            ) : (
              <select
                value={selectedFolder}
                onChange={(e) => setSelected(e.target.value)}
                data-testid="save-agreements-folder"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-shell-border bg-white outline-none focus:ring-2 focus:ring-primary/30"
              >
                <option value="">Root of Projects (no folder)</option>
                {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            )}
            <div className="mt-3 flex items-center gap-2">
              <input
                type="text"
                value={newFolder}
                onChange={(e) => setNewFolder(e.target.value)}
                placeholder="New folder name"
                data-testid="save-agreements-newfolder"
                className="flex-1 px-3 py-2 rounded-lg border border-shell-border outline-none focus:ring-2 focus:ring-primary/30 text-sm"
              />
              <button
                type="button"
                onClick={addFolder}
                disabled={creating || !newFolder.trim()}
                data-testid="save-agreements-create-folder"
                className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-lg bg-muted hover:bg-muted/70 disabled:opacity-40"
              >
                <FolderPlus className="w-3.5 h-3.5" /> Create
              </button>
            </div>
          </div>
        </div>

        <div className="p-4 bg-muted/40 border-t border-shell-border flex justify-end gap-2">
          <button type="button" onClick={() => onOpenChange?.(false)} className="px-4 py-2 rounded-lg text-sm font-semibold hover:bg-muted" data-testid="save-agreements-cancel">Cancel</button>
          <button
            type="button"
            onClick={confirm}
            disabled={saving}
            data-testid="save-agreements-confirm"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:opacity-90 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save Result
          </button>
        </div>
      </div>
    </div>
  );
}
